import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import CardsIcon from '@common/assets/icons/cards.svg?react';
import ChevroneRightIcon from '@common/assets/icons/chevrone_right.svg?react';
import CityIcon from '@common/assets/icons/city.svg?react';
import IncognitoIcon from '@common/assets/icons/incognito.svg?react';
import ListBulletsIcon from '@common/assets/icons/list_bullets.svg?react';
import PencilIcon from '@common/assets/icons/pencil.svg?react';
import SettingIcon from '@common/assets/icons/setting.svg?react';
import { Badge, type BadgeProps } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { IconButton } from '@common/ui/buttons/IconButton';
import { CachedImage } from '@common/ui/CachedImage';
import { Card } from '@common/ui/Card';
import { DynamicImage } from '@common/ui/DynamicImage';
import { Heading } from '@common/ui/typography/Heading';

import { useAccountStore } from '@entities/account';
import * as dicts from '@entities/profile/dicts';

import { type User, type VerificationStatus } from '@services/UsersService';

import { HiddenNotice, PremiumCard } from './components';
import styles from './MyProfileInfo.module.scss';

export interface MyProfileInfoProps {
  profile: User;
  verificationStatus: VerificationStatus;
  tasksCount: number;
}

export const MyProfileInfo = ({
  profile,
  tasksCount,
  verificationStatus,
}: MyProfileInfoProps) => {
  const navigate = useNavigate();
  const isHidden = useAccountStore((state) => state.account?.hidden === true);
  const completeness = calculateCompleteness(profile);
  const profileStatusTestId = `my-profile-info-status-${profile.approval_status?.status}`;

  const profileStatus = useMemo(() => {
    const progress: {
      color?: string;
      badgeColor?: BadgeProps['color'];
      progress?: number;
      text?: string;
      onClick?: () => void;
    } = {};

    switch (profile?.approval_status?.status) {
      case 'approved':
        if (!verificationStatus?.is_verified) {
          if (verificationStatus?.has_pending_request) {
            progress.color = 'var(--bg-action-primary)';
            progress.badgeColor = 'actionSecondary';
            progress.text = 'Проверяем';
            progress.progress = 50;
          } else {
            progress.color = 'var(--bg-action-primary)';
            progress.badgeColor = 'actionSecondary';
            progress.text = 'Подтвердите';
            progress.progress = 50;
            progress.onClick = () =>
              navigate({
                to: '/verification',
                search: {
                  gender: profile.sex === 0 ? 'male' : 'female',
                  canPass: false,
                },
              });
          }
        } else if (completeness < 50) {
          progress.color = 'var(--bg-negative-primary)';
          progress.badgeColor = 'negative';
          progress.text = `${completeness}%`;
          progress.progress = completeness;
        } else if (completeness < 90) {
          progress.color = 'var(--bg-warning-primary)';
          progress.badgeColor = 'warning';
          progress.text = `${completeness}%`;
          progress.progress = completeness;
        } else if (completeness <= 100) {
          progress.color = 'var(--bg-positive-primary)';
          progress.badgeColor = 'positive';
          progress.text = `${completeness}%`;
          progress.progress = completeness;
        }

        break;

      case 'rejected':
        progress.color = 'var(--bg-warning-primary)';
        progress.badgeColor = 'warning';
        progress.text = `Исправьте`;
        progress.progress = 75;
        progress.onClick = () =>
          navigate({
            to: '/profile/edit',
          });

        break;

      case 'pending':
      case 'pending_update':
        progress.color = 'var(--bg-action-primary)';
        progress.badgeColor = 'actionSecondary';
        progress.text = 'Проверяем';
        progress.progress = 50;

        break;

      default:
        break;
    }

    return progress;
  }, [profile]);

  // A light (matchmaker-only) account has no about text and no goal.
  const hasAbout = Boolean(profile.about?.trim());
  const goalText = profile.goal ? dicts.goalDisplayDict[profile.goal] : '';

  return (
    <div className={styles.myProfileInfo}>
      {isHidden && <HiddenNotice />}

      <Card testId="my-profile-info-base" className={styles.base}>
        <div
          className={styles.pictureContainer}
          onClick={profileStatus.onClick}
          data-test-id={profileStatusTestId}
        >
          <svg
            width="96"
            height="96"
            viewBox="0 0 96 96"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.progress}
          >
            <circle
              cx="48"
              cy="48"
              r="46"
              stroke={profileStatus.color}
              strokeWidth="4"
              fill="none"
              strokeDasharray={`${((profileStatus.progress || 0) / 100) * 289} 289`}
              strokeDashoffset="0"
              transform="rotate(90 48 48)"
            />
          </svg>
          <span className={styles.badgeContainer}>
            <Badge
              testId="my-profile-info-badge-base"
              color={profileStatus.badgeColor}
              text={profileStatus.text}
            />
          </span>

          {/* Beside the status badge, which keeps its meaning (and its tap,
              e.g. «Исправьте») while the profile is hidden. */}
          {isHidden && (
            <span className={styles.hiddenBadgeContainer}>
              <Badge
                testId="my-profile-info-badge-hidden"
                color="neutralSecondary"
                text="Скрыта"
              />
            </span>
          )}

          {profile?.photos && profile.photos[0] ? (
            <DynamicImage
              src={profile.photos[0]}
              className={styles.picture}
              alt={`profile-avatar-${profile?.username}`}
              testId="my-profile-info-avatar"
            />
          ) : (
            <IncognitoIcon className={styles.incognito} />
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.name}>
            {profile.username}, {profile.age}
            <div className={styles.icons}>
              {profile.is_premium && (
                <CachedImage
                  testId="cached-image"
                  alt="badge"
                  src="/icons/3d-icons/fire.png"
                  blur="2px"
                  style={{ width: '24px' }}
                />
              )}
              {profile.is_verified && (
                <CachedImage
                  testId="cached-image"
                  alt="badge"
                  src="/icons/3d-icons/confirmed.png"
                  blur="2px"
                  style={{ width: '24px' }}
                />
              )}
            </div>
          </div>
          <div className={styles.buttons}>
            <Link to="/profile/edit" data-test-id="my-profile-info-edit-link">
              <Button
                testId="my-profile-info-zodiac-calculate-button"
                variant="neutralSecondary"
                size={400}
              >
                Редактировать
              </Button>
            </Link>
            <Link
              to="/profile/settings"
              data-test-id="my-profile-info-settings-link"
            >
              <IconButton
                testId="my-profile-info-settings-button"
                variant="neutralSecondary"
                size={400}
                style={{
                  color: 'var(--graphic-tetriary-transparent)',
                }}
              >
                <SettingIcon />
              </IconButton>
            </Link>
          </div>
        </div>
      </Card>

      <PremiumCard
        has_premium={profile.is_premium}
        premium_expiration_date={profile.premium_until}
      />

      <Card testId="my-profile-info-payments">
        <Link
          to="/profile/payments"
          data-test-id="my-profile-info-payments-link"
        >
          <div className={styles.tasks}>
            <div>
              <CardsIcon />
              <span>Платежи</span>
            </div>
            <div className={styles.counter}>
              <ChevroneRightIcon />
            </div>
          </div>
        </Link>
      </Card>

      <Card testId="my-profile-info-tasks">
        <Link to="/tasks" data-test-id="my-profile-info-tasks-link">
          <div className={styles.tasks}>
            <div>
              <ListBulletsIcon />
              <span>Задания</span>
            </div>
            <div className={styles.counter}>
              {tasksCount > 0 && (
                <span data-test-id="my-profile-info-tasks-counter">
                  {tasksCount}
                </span>
              )}
              <ChevroneRightIcon />
            </div>
          </div>
        </Link>
      </Card>

      <Card testId="my-profile-info-about" className={styles.about}>
        <div className={styles.city}>
          <CityIcon /> {profile.city}
        </div>
        {hasAbout && (
          <div
            className={styles.about}
            data-test-id="my-profile-info-about-text"
          >
            <PencilIcon /> <span>{profile.about}</span>
          </div>
        )}
      </Card>

      {goalText && <Card testId="my-profile-info-goal">{goalText}</Card>}

      <Card testId="my-profile-info-short" className={styles.short}>
        <Heading level={3} style={{ padding: 0 }}>
          В двух словах
        </Heading>
        <div className={styles.listBadges}>
          {[
            dicts.zodiacSignDict[profile.zodiac],
            profile.height ? profile.height + ' см' : undefined,
            profile.views_on_alcohol !== undefined &&
              dicts.alcoholViewDict[profile.views_on_alcohol],
            profile.sports !== undefined && dicts.sportsDict[profile.sports],
            profile.children !== undefined &&
              dicts.childrenStatusDict[profile.children],
          ]
            .filter(Boolean)
            .map((item, index) => (
              <Badge
                testId="my-profile-info-badge-short"
                key={index}
                color="neutralSecondary"
                text={String(item)}
              />
            ))}
        </div>
      </Card>

      {profile?.interests?.length > 0 && (
        <Card testId="my-profile-info-interests" className={styles.interests}>
          <Heading level={3} style={{ padding: 0 }}>
            Интересы
          </Heading>
          <div className={styles.listBadges}>
            {profile.interests.map((interest, index) => (
              <Badge
                testId="my-profile-info-badge-interests"
                key={index}
                color="neutralSecondary"
                text={dicts.interestDict[interest]}
              />
            ))}
          </div>
        </Card>
      )}

      {profile?.study_and_work?.length > 0 && (
        <Card
          testId="my-profile-info-study-and-work"
          className={styles.studyAndWork}
        >
          <Heading level={3} style={{ padding: 0 }}>
            Учёба и работа
          </Heading>
          <div className={styles.listBadges}>
            {profile.study_and_work.map((item, index) => (
              <Badge
                testId="my-profile-info-badge-study-and-work"
                key={index}
                color="neutralSecondary"
                text={item}
              />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

function calculateCompleteness(user: User) {
  const excludeFields = [
    'is_premium',
    'is_verified',
    'id',
    'telegram_user_id',
    'premium_until',
    'approval_status',
    'notification_preferences',
    'last_latitude',
    'last_longitude',
    'last_location_update',
  ];

  const totalFields = Object.entries(user).filter(
    ([key]) => !excludeFields.includes(key),
  );

  const filledFields = totalFields.filter(([, val]) => {
    return (
      val !== undefined &&
      val !== null &&
      !(Array.isArray(val) && val.length === 0) &&
      !(typeof val === 'string' && val.trim() === '')
    );
  });

  const percentage = Math.round(
    (filledFields.length / totalFields.length) * 100,
  );

  return percentage;
}
